import { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import {
  useAuthActions,
  useHasUserManuallySignedOut,
  useIsAuthInitialized,
  useIsUserSignedIn,
} from "../../core_stores/useAuthStore";
import { useIsCompactView } from "../../core_stores/useIsCompactViewStore";
import { colors, typography } from "../../design/tokens";
import {
  SideMenuBehavior,
  useSideMenuBehavior,
} from "../../view_stores/useSideMenuStore";
import { ErrorScreenLayout } from "../common_components/ErrorScreenLayout";
import SideMenuPanel from "../sideMenu/SideMenuPanel";

const ProjectPageGuard = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const isUserSignedIn = useIsUserSignedIn();
  const isAuthInitialized = useIsAuthInitialized();
  const hasUserManuallySignedOut = useHasUserManuallySignedOut();
  const { hasAccessToProject } = useAuthActions();
  const sideMenuBehavior = useSideMenuBehavior();
  const isCompactView = useIsCompactView();
  const [hasProjectAccess, setHasProjectAccess] = useState<boolean | null>(
    null
  );
  const navigate = useNavigate();
  const isFirstRun = useRef(true);

  useEffect(() => {
    const checkAccess = async () => {
      setHasProjectAccess(null);
      if (!projectId) {
        setHasProjectAccess(false);
        return;
      }
      const hasAccess = await hasAccessToProject(projectId);
      setHasProjectAccess(hasAccess);
    };

    if (isAuthInitialized) {
      if (!isUserSignedIn) {
        navigate("/", {
          replace: true,
          state:
            !hasUserManuallySignedOut || isFirstRun.current
              ? {
                showAuthModal: true,
                redirectProjectId: projectId,
              }
              : undefined,
        });
      } else {
        checkAccess();
      }

      isFirstRun.current = false;
    }
  }, [isUserSignedIn, projectId, isAuthInitialized, hasUserManuallySignedOut]);

  if (hasProjectAccess === null) {
    return (
      <>
        {isUserSignedIn && !isCompactView && <SideMenuPanel />}
        <div
          className={`w-[100dvw] h-[100dvh]
      flex flex-col items-center justify-center
      ${colors.surface.primary}
      ${isUserSignedIn &&
            !isCompactView &&
            (sideMenuBehavior === SideMenuBehavior.PINNED
              ? "pl-[320px]"
              : "pl-[56px]")
            }`}
        >
          <h1
            className={`${typography.size["2xl"]} ${typography.weight.bold} ${colors.text.primary}`}
          >
            Loading...
          </h1>
        </div>
      </>
    );
  }

  if (hasProjectAccess === false) {
    return (
      <ErrorScreenLayout
        isLoading={false}
        info="You don't have permission to access this project"
        actionBtn={{
          label: "Go back to Home page",
          onClick: () => navigate("/", { replace: true }),
        }}
      />
    );
  }

  return <Outlet />;
};

export default ProjectPageGuard;
